import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LogOut, RefreshCw, Eye, MessageSquare, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pa_admin_token";

const inputClass =
  "w-full rounded-none border border-white/15 bg-transparent px-4 py-3 font-tty text-sm text-white placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-acid";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [contacts, setContacts] = useState(null);
  const [stats, setStats] = useState(null);

  const loadData = useCallback(async (tk) => {
    try {
      const headers = { Authorization: `Bearer ${tk}` };
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers }),
      ]);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
    }
  }, []);

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro de autenticação");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setContacts(null);
    setStats(null);
  };

  const markRead = async (id) => {
    await axios.patch(`${API}/admin/contacts/${id}/read`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setContacts((c) => c.map((x) => (x.id === id ? { ...x, read: true } : x)));
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login-page">
        <form onSubmit={handleLogin} className="w-full max-w-sm border border-white/10 bg-surface p-8" data-testid="admin-login-form">
          <p className="flex items-baseline gap-1">
            <span className="font-tty text-2xl font-bold text-white">Libertec</span>
            <span className="animate-caret font-tty text-2xl font-bold text-acid">_</span>
          </p>
          <p className="mb-8 mt-2 font-tty text-[10px] uppercase tracking-[0.3em] text-neutral-500">backoffice — acesso restrito</p>
          <div className="space-y-4">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" data-testid="admin-email-input" className={inputClass} />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" data-testid="admin-password-input" className={inputClass} />
          </div>
          {error && <p className="mt-4 font-tty text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-button" className="mt-6 w-full bg-acid py-3.5 font-tty text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white">
            Entrar
          </button>
          <a href="/" className="mt-6 block text-center font-tty text-[10px] uppercase tracking-widest text-neutral-500 transition-colors hover:text-acid" data-testid="admin-back-link">
            ← voltar ao site
          </a>
        </form>
      </div>
    );
  }

  const maxDaily = stats?.daily?.length ? Math.max(...stats.daily.map((d) => d.count), 1) : 1;

  return (
    <div className="min-h-screen bg-ink px-6 py-12 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-baseline gap-1">
              <span className="font-tty text-2xl font-bold text-white">Libertec</span>
              <span className="animate-caret font-tty text-2xl font-bold text-acid">_</span>
            </p>
            <p className="mt-1 font-tty text-[10px] uppercase tracking-[0.3em] text-neutral-500">backoffice</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => loadData(token)} data-testid="admin-refresh-button" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-tty text-[11px] uppercase tracking-widest text-neutral-300 transition-colors hover:border-acid hover:text-acid">
              <RefreshCw className="h-3.5 w-3.5" /> Atualizar
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-tty text-[11px] uppercase tracking-widest text-neutral-300 transition-colors hover:border-red-400 hover:text-red-400">
              <LogOut className="h-3.5 w-3.5" /> Sair
            </button>
          </div>
        </div>

        {stats && (
          <section className="mb-12" data-testid="analytics-panel">
            <p className="mb-4 font-tty text-[10px] uppercase tracking-[0.3em] text-neutral-500">estatísticas anónimas — últimos 90 dias</p>
            <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-5">
              {[
                { label: "visitas", value: stats.total_pageviews, icon: Eye, color: "text-acid" },
                { label: "hoje", value: stats.today, icon: Eye, color: "text-brand-blue" },
                { label: "7 dias", value: stats.last7, icon: Eye, color: "text-brand-blue" },
                { label: "chats", value: stats.chat_sessions, icon: MessageSquare, color: "text-brand-green" },
                { label: "pedidos", value: stats.contacts_total, icon: Mail, color: "text-acid" },
              ].map((card) => (
                <div key={card.label} className="bg-ink p-5" data-testid={`stats-card-${card.label}`}>
                  <card.icon className={`h-4 w-4 ${card.color}`} />
                  <p className="mt-3 font-display text-3xl font-medium text-white">{card.value}</p>
                  <p className="mt-1 font-tty text-[10px] uppercase tracking-[0.2em] text-neutral-500">{card.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="border border-white/10 p-5" data-testid="stats-daily">
                <p className="mb-4 font-tty text-[10px] uppercase tracking-[0.2em] text-neutral-500">visitas por dia (14d)</p>
                {stats.daily.length === 0 ? (
                  <p className="font-tty text-xs text-neutral-600">sem dados ainda</p>
                ) : (
                  <div className="flex h-20 items-end gap-1">
                    {stats.daily.map((d) => (
                      <div key={d.date} className="flex-1 bg-acid/80 transition-colors hover:bg-acid" style={{ height: `${Math.max((d.count / maxDaily) * 100, 4)}%` }} title={`${d.date}: ${d.count}`} />
                    ))}
                  </div>
                )}
              </div>
              <div className="border border-white/10 p-5" data-testid="stats-referrers">
                <p className="mb-4 font-tty text-[10px] uppercase tracking-[0.2em] text-neutral-500">origens</p>
                {stats.top_referrers.length === 0 ? (
                  <p className="font-tty text-xs text-neutral-600">sem referências externas ainda</p>
                ) : (
                  <ul className="space-y-2 font-tty text-xs text-neutral-300">
                    {stats.top_referrers.map(([host, count]) => (
                      <li key={host} className="flex justify-between"><span className="text-brand-blue">{host}</span><span>{count}</span></li>
                    ))}
                  </ul>
                )}
                <p className="mb-2 mt-6 font-tty text-[10px] uppercase tracking-[0.2em] text-neutral-500">idiomas</p>
                <ul className="space-y-2 font-tty text-xs text-neutral-300">
                  {stats.langs.map(([l, count]) => (
                    <li key={l} className="flex justify-between"><span className="uppercase">{l}</span><span>{count}</span></li>
                  ))}
                </ul>
              </div>
              <div className="border border-white/10 p-5" data-testid="stats-devices">
                <p className="mb-4 font-tty text-[10px] uppercase tracking-[0.2em] text-neutral-500">dispositivos</p>
                <ul className="space-y-2 font-tty text-xs text-neutral-300">
                  {stats.devices.map(([d, count]) => (
                    <li key={d} className="flex justify-between"><span>{d === "mobile" ? "telemóvel" : "computador"}</span><span>{count}</span></li>
                  ))}
                </ul>
                <p className="mb-2 mt-6 font-tty text-[10px] uppercase tracking-[0.2em] text-neutral-500">páginas</p>
                <ul className="space-y-2 font-tty text-xs text-neutral-300">
                  {stats.top_pages.map(([p, count]) => (
                    <li key={p} className="flex justify-between"><span>{p}</span><span>{count}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        <p className="mb-4 font-tty text-[10px] uppercase tracking-[0.3em] text-neutral-500">pedidos de contacto</p>
        {contacts === null ? (
          <p className="font-tty text-sm text-neutral-500">a carregar…</p>
        ) : contacts.length === 0 ? (
          <p className="border border-white/10 p-12 text-center font-tty text-sm text-neutral-500" data-testid="contacts-empty">Sem pedidos de contacto ainda.</p>
        ) : (
          <div className="overflow-x-auto border border-white/10" data-testid="contacts-table">
            <table className="w-full min-w-[800px] text-left font-tty text-xs">
              <thead>
                <tr className="border-b border-white/10 bg-surface text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Empresa</th>
                  <th className="px-4 py-3">Serviço</th>
                  <th className="px-4 py-3">Mensagem</th>
                  <th className="px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className={`border-b border-white/5 transition-colors hover:bg-surface ${c.read ? "opacity-50" : ""}`} data-testid={`contact-row-${c.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-neutral-500">{new Date(c.created_at).toLocaleString("pt-PT")}</td>
                    <td className="px-4 py-3 text-white">{c.name}</td>
                    <td className="px-4 py-3 text-acid">{c.email}</td>
                    <td className="px-4 py-3 text-neutral-300">{c.company || "—"}</td>
                    <td className="px-4 py-3 text-neutral-300">{c.service || "—"}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-neutral-400" title={c.message}>{c.message}</td>
                    <td className="px-4 py-3">
                      {c.read ? (
                        <span className="text-neutral-500">lido</span>
                      ) : (
                        <button onClick={() => markRead(c.id)} data-testid={`mark-read-${c.id}`} className="border border-acid/40 px-2 py-1 text-[10px] uppercase tracking-widest text-acid transition-colors hover:bg-acid hover:text-black">
                          marcar lido
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
